import {
  FeatureEntitlementName,
  FeatureEntitlementStatus,
} from '@jupiterone/entitlement-types';
import { differenceInDays } from '../util/dateFns';
import { useEntitlementConfigurationQuery } from './useEntitlementConfiguration';
import { useStartTrialForFeatureEntitlementMutation } from './useStartTrialForFeatureEntitlementMutation';

export const useEntitlements = () => {
  const { data, loading, refetch } = useEntitlementConfigurationQuery();

  const entitlementConfig = data?.getEntitlementConfiguration;

  const isActiveTrialFeatureEntitlement = (
    entitlementName: FeatureEntitlementName
  ): boolean => {
    if (!entitlementConfig) return false;
    const status =
      entitlementConfig.featureEntitlements?.[entitlementName]?.status;
    const trialEndTime =
      entitlementConfig.featureEntitlements?.[entitlementName]?.trialEndTime ||
      0;

    return (
      status === FeatureEntitlementStatus.TRIAL &&
      Boolean(trialEndTime) &&
      differenceInDays(new Date(trialEndTime), new Date()) >= 0
    );
  };

  const hasValidAccessFeatureEntitlement = (
    featureEntitlementName: FeatureEntitlementName
  ): boolean => {
    if (entitlementConfig) {
      const entitlementStatus =
        entitlementConfig.featureEntitlements?.[featureEntitlementName]?.status;

      // A valid entitlement has a status of Full or an active trial
      // all others should be denied access
      return (
        entitlementStatus === FeatureEntitlementStatus.FULL_ACCESS ||
        isActiveTrialFeatureEntitlement(featureEntitlementName)
      );
    }

    return false;
  };

  const featureEntitlementsThatCanStartTrial = (
    featureEntitlementNames: FeatureEntitlementName[]
  ) => {
    if (entitlementConfig && entitlementConfig.featureEntitlements) {
      return Object.keys(entitlementConfig.featureEntitlements).filter(
        (featureEntitlementName) => {
          if (
            featureEntitlementNames.includes(
              featureEntitlementName as FeatureEntitlementName
            )
          ) {
            const featureEntitlement = entitlementConfig.featureEntitlements![
              featureEntitlementName as FeatureEntitlementName
            ];

            // if you have a trialEndDate, that means you already have had a trial (or are currently in one)
            // if you have a trialStartDate, that means that you have already started one, so cannot start another
            const alreadyStartedTrial =
              Boolean(featureEntitlement?.trialStartTime) ||
              Boolean(featureEntitlement?.trialEndTime) ||
              featureEntitlement?.status ===
                FeatureEntitlementStatus.TRIAL_EXPIRED;

            // Can only start a trial when you haven't already started one, and your entitlement status is none
            return (
              !alreadyStartedTrial &&
              entitlementConfig.featureEntitlements![
                featureEntitlementName as FeatureEntitlementName
              ]?.status === FeatureEntitlementStatus.NO_ACCESS
            );
          }
          return false;
        }
      );
    } else {
      return [];
    }
  };

  const hasAnyValidFeatureEntitlement = (
    featureEntitlementNames: FeatureEntitlementName[]
  ): boolean => {
    return featureEntitlementNames.some((featureEntitlementName) =>
      hasValidAccessFeatureEntitlement(featureEntitlementName)
    );
  };

  const hasAllValidFeatureEntitlements = (
    featureEntitlementNames: FeatureEntitlementName[]
  ): boolean => {
    return featureEntitlementNames.every((featureEntitlementName) =>
      hasValidAccessFeatureEntitlement(featureEntitlementName)
    );
  };

  const isTrialFeatureEntitlement = (
    featureEntitlementName: FeatureEntitlementName
  ): boolean => {
    if (!entitlementConfig) return false;

    return (
      entitlementConfig.featureEntitlements?.[featureEntitlementName]
        ?.status === FeatureEntitlementStatus.TRIAL
    );
  };

  const getTrialEndDate = (
    featureEntitlementName: FeatureEntitlementName
  ): string | undefined => {
    return entitlementConfig?.featureEntitlements?.[featureEntitlementName]
      ?.trialEndTime;
  };

  const [
    startTrialForFeatureEntitlementMutation,
  ] = useStartTrialForFeatureEntitlementMutation();
  const startFeatureEntitlementTrial = async ({
    featureEntitlementName,
  }: {
    featureEntitlementName: FeatureEntitlementName;
  }): Promise<void> => {
    if (!loading) {
      const {
        data: mutationResponse,
        errors: mutationErrors,
      } = await startTrialForFeatureEntitlementMutation({
        variables: {
          featureEntitlementName,
        },
      });

      if (
        (mutationErrors && mutationErrors.length > 0) ||
        mutationResponse?.startFeatureEntitlementTrial.resultCode === 'FAILED'
      ) {
        throw new Error(`Failed to start trial for ${featureEntitlementName}`);
      } else {
        await refetch();
        return;
      }
    }
  };

  return {
    entitlementConfig,
    loading,
    entitlementEnforce: hasValidAccessFeatureEntitlement,
    entitlementEnforceAny: hasAnyValidFeatureEntitlement,
    entitlementEnforceAll: hasAllValidFeatureEntitlements,
    featureEntitlementsThatCanStartTrial,
    isTrialFeatureEntitlement,
    getTrialEndDate,
    startFeatureEntitlementTrial,
  };
};
